import {
  forwardRef,
  ButtonHTMLAttributes,
  AnchorHTMLAttributes,
  ReactNode,
} from 'react';
import classNames from 'classnames';
import styles from './button.module.sass';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'default' | 'compact';

type BaseProps = {
  /** Visual style variant */
  variant?: ButtonVariant;
  /** Size: default (40px) or compact (32px) */
  size?: ButtonSize;
  /** Show loading spinner and disable interactions */
  loading?: boolean;
  /** Icon element placed before children */
  icon?: ReactNode;
  /** Render as icon-only button (no text) */
  iconOnly?: boolean;
  /** Additional CSS class */
  className?: string;
  children?: ReactNode;
};

type AsButton = BaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseProps> & {
    href?: never;
  };

type AsAnchor = BaseProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof BaseProps> & {
    href: string;
  };

export type ButtonProps = AsButton | AsAnchor;

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'default',
      loading = false,
      icon,
      iconOnly = false,
      className,
      children,
      ...rest
    },
    ref
  ) => {
    const classes = classNames(
      styles.btn,
      styles[variant],
      {
        [styles.compact]: size === 'compact',
        [styles.loading]: loading,
        [styles.hasIcon]: !!icon && !iconOnly,
        [styles.iconOnly]: iconOnly,
      },
      className
    );

    if ('href' in rest && rest.href) {
      const { href, ...anchorProps } = rest as AsAnchor;
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          className={classes}
          {...anchorProps}
        >
          {icon}
          {!iconOnly && children}
        </a>
      );
    }

    const buttonProps = rest as AsButton;
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        className={classes}
        disabled={buttonProps.disabled || loading}
        {...buttonProps}
      >
        {icon}
        {!iconOnly && children}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
